import React, { useState } from 'react';
import { API } from 'aws-amplify';

import { Driver } from '../../context/model/types';
import { updateDriver } from '../../graphql/mutations';
import {
  DriverSchedule,
  DayConfig,
  SlotConfig,
  SlotKey,
  DEFAULT_SLOT,
  parseSchedule,
} from '../../utils/driverAvailability';

interface Props {
  driver: Driver;
  setShow: (v: boolean) => void;
  onSave: (updated: Driver) => void;
}

const DAYS: { label: string; dow: string }[] = [
  { label: 'Monday',    dow: '1' },
  { label: 'Tuesday',   dow: '2' },
  { label: 'Wednesday', dow: '3' },
  { label: 'Thursday',  dow: '4' },
  { label: 'Friday',    dow: '5' },
  { label: 'Saturday',  dow: '6' },
  { label: 'Sunday',    dow: '0' },
];

const freshDay = (): DayConfig => ({
  morning:   { ...DEFAULT_SLOT },
  afternoon: { ...DEFAULT_SLOT },
});

const DriverAvailabilityModal: React.FC<Props> = ({ driver, setShow, onSave }) => {
  const initial = parseSchedule(driver.availability);

  const [schedule, setSchedule] = useState<DriverSchedule>(() => {
    const s: DriverSchedule = {};
    DAYS.forEach(({ dow }) => { s[dow] = initial[dow] ?? null; });
    return s;
  });
  const [calendarUrl, setCalendarUrl] = useState<string>(driver.googleCalendarUrl ?? '');
  const [saving, setSaving] = useState(false);

  const toggleDay = (dow: string, active: boolean) => {
    setSchedule(prev => ({ ...prev, [dow]: active ? freshDay() : null }));
  };

  const patchSlot = (dow: string, slot: SlotKey, patch: Partial<SlotConfig>) => {
    setSchedule(prev => {
      const day = prev[dow] ?? freshDay();
      return { ...prev, [dow]: { ...day, [slot]: { ...day[slot], ...patch } } };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const clean: DriverSchedule = {};
      DAYS.forEach(({ dow }) => {
        const day = schedule[dow];
        if (!day || (!day.morning.enabled && !day.afternoon.enabled)) {
          clean[dow] = null;
        } else {
          clean[dow] = day;
        }
      });

      const cleanedUrl = calendarUrl.trim() || null;

      await API.graphql({
        query: updateDriver,
        variables: {
          input: {
            id: driver.id,
            availability: JSON.stringify(clean),
            googleCalendarUrl: cleanedUrl,
          },
        },
      });

      onSave({ ...driver, availability: JSON.stringify(clean), googleCalendarUrl: cleanedUrl });
      setShow(false);
    } catch (e) {
      console.error('Error saving availability:', e);
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        backgroundColor: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
      onClick={e => { if (e.target === e.currentTarget) setShow(false); }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: 10,
          padding: '24px 28px',
          width: '100%',
          maxWidth: 540,
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        }}
      >
        <h2 style={{ margin: '0 0 2px', fontSize: 17, fontWeight: 600, color: '#111827' }}>
          Edit Schedule
        </h2>
        <p style={{ margin: '0 0 20px', fontSize: 13, color: '#6B7280' }}>{driver.name}</p>

        <div style={{ marginBottom: 20 }}>
          <label
            htmlFor="googleCalendarUrl"
            style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#111827', marginBottom: 4 }}
          >
            Google Calendar (iCal URL)
          </label>
          <input
            id="googleCalendarUrl"
            type="url"
            value={calendarUrl}
            onChange={e => setCalendarUrl(e.target.value)}
            placeholder="https://calendar.google.com/calendar/ical/.../basic.ics"
            style={{
              width: '100%',
              padding: '8px 10px',
              borderRadius: 6,
              border: '1px solid #D1D5DB',
              fontSize: 13,
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
          <p style={{ margin: '6px 0 0', fontSize: 11, color: '#6B7280' }}>
            Paste your Google Calendar private iCal URL. Events sync hourly; conflicting events auto-block your AM (9–11am) or PM (1–3pm) slots.
          </p>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th()}>Day</th>
              <th style={th()}>Morning (9–11am)</th>
              <th style={th()}>Afternoon (1–3pm)</th>
            </tr>
          </thead>
          <tbody>
            {DAYS.map(({ label, dow }) => {
              const dayActive = schedule[dow] !== null;
              const day = schedule[dow] ?? freshDay();
              return (
                <tr key={dow} style={{ borderBottom: '1px solid #F3F4F6' }}>
                  <td style={td()}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={dayActive}
                        onChange={e => toggleDay(dow, e.target.checked)}
                        style={{ accentColor: '#7B1FA2', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{label}</span>
                    </label>
                  </td>
                  {(['morning', 'afternoon'] as SlotKey[]).map(slot => (
                    <td key={slot} style={td()}>
                      {!dayActive ? (
                        <span style={{ color: '#D1D5DB', fontSize: 12 }}>—</span>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                          <label style={checkLabel()}>
                            <input
                              type="checkbox"
                              checked={day[slot].enabled}
                              onChange={e => patchSlot(dow, slot, { enabled: e.target.checked })}
                              style={{ accentColor: '#7B1FA2', cursor: 'pointer' }}
                            />
                            <span>Enabled</span>
                          </label>
                          {day[slot].enabled && (
                            <label style={checkLabel()}>
                              <input
                                type="checkbox"
                                checked={day[slot].paid}
                                onChange={e => patchSlot(dow, slot, { paid: e.target.checked })}
                                style={{ accentColor: '#7B1FA2', cursor: 'pointer' }}
                              />
                              <span>Paid</span>
                            </label>
                          )}
                        </div>
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button
            onClick={() => setShow(false)}
            style={{
              padding: '8px 16px', borderRadius: 6,
              border: '1px solid #D1D5DB', background: '#fff',
              fontSize: 13, cursor: 'pointer', color: '#374151',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '8px 16px', borderRadius: 6, border: 'none',
              background: saving ? '#C084FC' : '#7B1FA2',
              color: '#fff', fontSize: 13, fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer',
            }}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

const th = (): React.CSSProperties => ({
  padding: '8px 10px',
  fontSize: 12,
  fontWeight: 500,
  color: '#6B7280',
  textAlign: 'left',
  backgroundColor: '#F9FAFB',
  borderBottom: '1px solid #E5E7EB',
  whiteSpace: 'nowrap',
});

const td = (): React.CSSProperties => ({
  padding: '10px 10px',
  verticalAlign: 'top',
});

const checkLabel = (): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  fontSize: 12,
  color: '#374151',
  cursor: 'pointer',
  userSelect: 'none',
});

export default DriverAvailabilityModal;
